<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const cpfValidator = helpers.withMessage(
  'CPF deve ter 11 dígitos',
  value => !value || value.replace(/\D/g, '').length === 11
);

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  CPF: { field: 'additionalAttributes.cpf' },
  FIRST_SERVICE_STAGE: { field: 'additionalAttributes.firstServiceStage' },
  MATCH_APARELHO: { field: 'matchAparelho', type: 'boolean' },
  APROVADO: { field: 'aprovado', type: 'boolean' },
  PLANO_ATUAL: { field: 'planoAtual' },
  // COUNTRY: { field: 'additionalAttributes.countryCode' }, // Remove this line
  // BIO: { field: 'additionalAttributes.description' },
  // COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  matchAparelho: null,
  aprovado: null,
  planoAtual: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    cpf: '',
    firstServiceStage: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

const validationRules = {
  firstName: { required },
  email: { email },
};

// Validação para campos aninhados
const additionalAttributesValidation = {
  cpf: { cpfValidator },
};

const v$ = useVuelidate(validationRules, state);
const vAdditional$ = useVuelidate(additionalAttributesValidation, state.additionalAttributes);

const isFormInvalid = computed(() => v$.value.$invalid || vAdditional$.value.$invalid);

const formatCpf = value => {
  if (!value) return '';
  // Remove tudo que não é dígito
  const cleaned = value.replace(/\D/g, '');
  // Aplica a máscara XXX.XXX.XXX-XX
  if (cleaned.length <= 11) {
    return cleaned
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return cleaned
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    matchAparelho = null,  // camelCase do store
    aprovado = null,
    planoAtual = '',
    additionalAttributes = {},
  } = props.contactData || {};
  
  // Debug: verificar valores recebidos
  console.log('[ContactsForm] prepareStateBasedOnProps - Props recebidas:', {
    matchAparelho: matchAparelho,
    aprovado: aprovado,
    planoAtual: planoAtual,
    contactData: props.contactData
  });
  
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    cpf = '',
    firstServiceStage = '',
    first_service_stage = '',
    socialProfiles = {},
  } = additionalAttributes || {};
  
  // Aceita ambos os formatos (snake_case do backend ou camelCase)
  const firstServiceStageValue = firstServiceStage || first_service_stage || '';

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    matchAparelho,
    aprovado,
    planoAtual,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      cpf: formatCpf(cpf),
      firstServiceStage: firstServiceStageValue,
      socialProfiles,
    },
  });
  
  // Debug: verificar estado após atribuição
  console.log('[ContactsForm] prepareStateBasedOnProps - Estado após assign:', {
    matchAparelho: state.matchAparelho,
    aprovado: state.aprovado,
    planoAtual: state.planoAtual
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const matchAparelhoOptions = computed(() => [
  { label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.MATCH_APARELHO.EMPTY'), value: null },
  { label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.MATCH_APARELHO.TRUE'), value: true },
  { label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.MATCH_APARELHO.FALSE'), value: false },
]);

const aprovadoOptions = computed(() => [
  { label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.APROVADO.EMPTY'), value: null },
  { label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.APROVADO.TRUE'), value: true },
  { label: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.APROVADO.FALSE'), value: false },
]);

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email', 'additionalAttributes.cpf'].includes(field);
};

const getValidationKey = key => {
  const field = FORM_CONFIG[key]?.field;
  // Para campos aninhados, retorna apenas a última parte
  if (field === 'additionalAttributes.cpf') return 'cpf';
  return field;
};

// Prepara o estado para o backend (Rails converte automaticamente camelCase → snake_case)
const prepareStateForBackend = stateData => {
  const { firstName, lastName, ...stateWithoutNames } = stateData;
  const prepared = {
    ...stateWithoutNames,
    additionalAttributes: {
      ...stateWithoutNames.additionalAttributes,
      cpf: (stateWithoutNames.additionalAttributes.cpf || '').replace(/\D/g, ''),
    },
  };
  
  // Debug log
  console.log('[ContactsForm] prepareStateForBackend:', {
    matchAparelho: prepared.matchAparelho,
    aprovado: prepared.aprovado,
    full: prepared
  });
  
  return prepared;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle boolean fields
      if (FORM_CONFIG[key]?.type === 'boolean') {
        const value = state[field];
        console.log(`[ContactsForm] getFormBinding GET - key: ${key}, field: ${field}, value:`, value);
        return value;
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else if (FORM_CONFIG[key]?.type === 'boolean') {
        // Handle boolean fields
        console.log(`[ContactsForm] getFormBinding SET - key: ${key}, field: ${field}, value:`, value);
        state[field] = value;
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Aplica máscara para CPF
          if (nested === 'cpf') {
            state[base][nested] = formatCpf(value);
          } else {
            // Example: additionalAttributes.city = "New York"
            state[base][nested] = value;
          }
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      const isFormValid = await v$.value.$validate();
      const isAdditionalValid = await vAdditional$.value.$validate();
      if (isFormValid && isAdditionalValid) {
        emit('update', prepareStateForBackend(state));
      }
    },
  });
};

const getMessageType = key => {
  const field = FORM_CONFIG[key]?.field;
  const validationKey = getValidationKey(key);
  
  // Para campos em additionalAttributes, usa vAdditional$
  if (field?.startsWith('additionalAttributes.')) {
    return isValidationField(key) && vAdditional$.value[validationKey]?.$error
      ? 'error'
      : 'info';
  }
  
  // Para campos normais, usa v$
  return isValidationField(key) && v$.value[validationKey]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', prepareStateForBackend(state));
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <!-- Removed ComboBox for COUNTRY -->
          <PhoneNumberInput
            v-if="item.key === 'PHONE_NUMBER'"
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :show-border="isDetailsView"
          />
          <ComboBox
            v-else-if="item.key === 'MATCH_APARELHO'"
            v-model="getFormBinding(item.key).value"
            :options="matchAparelhoOptions"
            :placeholder="item.placeholder"
            class="w-full"
          />
          <ComboBox
            v-else-if="item.key === 'APROVADO'"
            v-model="getFormBinding(item.key).value"
            :options="aprovadoOptions"
            :placeholder="item.placeholder"
            class="w-full"
          />
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :placeholder="item.placeholder"
            :message-type="getMessageType(item.key)"
            :maxlength="item.key === 'CPF' ? 14 : undefined"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="
              isValidationField(item.key) &&
                (FORM_CONFIG[item.key]?.field?.startsWith('additionalAttributes.')
                  ? vAdditional$[getValidationKey(item.key)].$touch()
                  : v$[getValidationKey(item.key)].$touch())
            "
            @blur="
              isValidationField(item.key) &&
                (FORM_CONFIG[item.key]?.field?.startsWith('additionalAttributes.')
                  ? vAdditional$[getValidationKey(item.key)].$touch()
                  : v$[getValidationKey(item.key)].$touch())
            "
          />
        </template>
      </div>
    </div>
    <!-- <div class="flex flex-col items-start gap-2">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
      </span>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div> -->
  </div>
</template>
